import React, { useState, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const AccountAccess = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Styles adaptatifs pour le thème
  const isDark = theme === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `/api/user/${user}/password`,
        {
          password: newPassword,
          oldPassword: oldPassword,
        },
        config
      );
      setSuccess('Mot de passe modifié avec succès !');
      // Réinitialiser les champs après succès
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la modification du mot de passe.');
    }
  };

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour modifier votre mot de passe.</Alert>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ color: isDark ? '#90caf9' : 'inherit' }}
        >
          Modifier mon mot de passe
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Mot de passe actuel"
            type={showOldPassword ? 'text' : 'password'}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiInputLabel-root': {
                color: isDark ? '#90caf9' : 'inherit'
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                color: isDark ? '#fff' : 'inherit',
                '& fieldset': {
                  borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit'
                },
                '&:hover fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                }
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    edge="end"
                    sx={{ color: isDark ? '#90caf9' : 'inherit' }}
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Nouveau mot de passe"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiInputLabel-root': {
                color: isDark ? '#90caf9' : 'inherit'
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                color: isDark ? '#fff' : 'inherit',
                '& fieldset': {
                  borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit'
                },
                '&:hover fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                }
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    sx={{ color: isDark ? '#90caf9' : 'inherit' }}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirmer le nouveau mot de passe"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiInputLabel-root': {
                color: isDark ? '#90caf9' : 'inherit'
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                color: isDark ? '#fff' : 'inherit',
                '& fieldset': {
                  borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit'
                },
                '&:hover fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                }
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    sx={{ color: isDark ? '#90caf9' : 'inherit' }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ 
              mt: 3,
              bgcolor: isDark ? '#90caf9' : 'inherit',
              color: isDark ? '#1a253c' : 'inherit',
              '&:hover': {
                bgcolor: isDark ? '#64b5f6' : 'inherit'
              }
            }}
            fullWidth
          >
            Modifier
          </Button>
        </Box>
      </CardContent>
    </Box>
  );
};

export default AccountAccess;